// "Where does the signal come from" (UIC-3, the user's comment on the
// prototype): every card of the overview and every item of "В эфире сейчас"
// says it in words - "SRT :9027 ← 10.0.0.5", "копия потока encoder2",
// "YouTube по ссылке", "берём с ingest · cdn1.example". Pure functions over a
// console channel (configuration + live input status) or a Flussonic API
// stream (its inputs). Addresses are cut to scheme/host/port, never a key
// (principle 7): parseEndpoint is the same one the wall uses.

import type { Channel, ChannelStatus, ID, Input, InputStatus } from '@/api/types'
import { parseEndpoint, platformOf } from '@/features/wall/flsModel'
import { activeInputStatus } from '@/features/wall/model'
import type { LocalStream } from '@/features/fls/model'
import { common } from '@/shared/i18n/messages'
import { messages } from './i18n'

/** Hosts of the sites ("cdn1.example" -> "ingest"): a pull from one of our own sites names the site. */
export type SiteHosts = ReadonlyMap<string, string>

const LOOPBACK = new Set(['127.0.0.1', 'localhost', '::1', '[::1]'])
const ANY_HOST = new Set(['', '0.0.0.0', '[::]', '::', '*'])
const PREFIXED = /^(m4s|hlss):(?=https?:)/i
const FIRST_SEGMENT = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*\/([^/?#]+)/i

/** "10.0.0.5:51234" / "[2001:db8::1]:5000" -> the address without the port. */
export function senderIp(remote: string | undefined): string {
  const r = (remote ?? '').trim()
  if (r === '') return ''
  const v6 = /^\[([^\]]+)\](?::\d+)?$/.exec(r)
  if (v6) return v6[1]!
  const v4 = /^([^:]+):\d+$/.exec(r)
  return v4 ? v4[1]! : r
}

/**
 * The stream an address copies from, when it is one of this Flussonic's own:
 * `copy://encoder2` or the relay `m4s://127.0.0.1/encoder2`; null otherwise.
 */
export function copyName(rawUrl: string): string | null {
  const url = rawUrl.trim().replace(PREFIXED, '')
  const e = parseEndpoint(url)
  if (!e) return null
  if (e.scheme === 'copy') return /^copy:\/\/([^/?#\s]+)/i.exec(url)?.[1] ?? null
  if (LOOPBACK.has(e.host)) {
    const name = FIRST_SEGMENT.exec(url)?.[1]
    return name ? decodeSegment(name) : null
  }
  return null
}

/** The origin of one Flussonic API input address. */
export function urlOrigin(rawUrl: string, hosts: SiteHosts = new Map()): string {
  const url = rawUrl.trim().replace(PREFIXED, '')
  const e = parseEndpoint(url)
  if (!e) return rawUrl.trim() === '' ? messages.t('originNone') : messages.t('originUnreadable')
  const copy = copyName(url)
  if (copy) return messages.t('originCopy', { name: copy })
  if (e.scheme === 'copy') return messages.t('originUnreadable')
  if (e.scheme === 'publish') return messages.t('originPublish')
  if (ANY_HOST.has(e.host)) {
    // srt://:9027 - the stream listens, a sender comes to it.
    const proto = listenerProto(e.scheme)
    return e.port ? messages.t('originListen', { proto, port: e.port }) : messages.t('originListenNoPort', { proto })
  }
  const platform = platformOf(e.host)
  if (platform) return messages.t('originPlatform', { platform })
  const site = hosts.get(e.host)
  if (site) return messages.t('originSiteHost', { site, host: e.host })
  return messages.t('originPull', { host: e.port ? `${e.host}:${e.port}` : e.host })
}

function decodeSegment(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

function listenerProto(scheme: string): string {
  switch (scheme) {
    case 'srt':
      return 'SRT'
    case 'rtmp':
    case 'rtmps':
      return 'RTMP'
    case 'udp':
    case 'rtp':
      return scheme.toUpperCase()
    default:
      return scheme.toUpperCase()
  }
}

/** "45 с", "12 мин", "3 ч": how long ago, one unit (FLS-INFO). Nothing for a missing or future instant. */
export function shortAge(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return ''
  if (sec < 60) return `${Math.floor(sec)} ${common.t('unitS')}`
  if (sec < 3600) return `${Math.floor(sec / 60)} ${common.t('unitMin')}`
  return `${Math.floor(sec / 3600)} ${common.t('unitH')}`
}

/** Seconds from an RFC 3339 instant to now (ms); null when there is none. */
export function ageSec(iso: string | undefined, now: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) || t <= 0 ? null : Math.max(0, (now - t) / 1000)
}

/**
 * The origin of a Flussonic API stream: its active input, else the first one.
 * A publish:// input with a connected sender (FLS-INFO) names the sender's
 * address, and how long it has been connected when `now` (epoch ms) is given.
 */
export function streamOrigin(
  stream: Pick<LocalStream, 'inputs'>,
  hosts: SiteHosts = new Map(),
  now?: number,
): string {
  const inputs = stream.inputs ?? []
  const input = inputs.find((i) => i.active) ?? inputs[0]
  if (!input) return messages.t('originNone')
  if (input.sender && parseEndpoint(input.url)?.scheme === 'publish') {
    const ip = senderIp(input.sender)
    const age = now === undefined ? null : ageSec(input.since, now)
    return age === null
      ? messages.t('originPublishFrom', { ip })
      : messages.t('originPublishFromFor', { ip, age: shortAge(age) })
  }
  return urlOrigin(input.url, hosts)
}

/** The origin of a console channel: the input that feeds it now, else its first configured input. */
export function channelOrigin(channel: Pick<Channel, 'inputs'>, status: ChannelStatus | undefined, hosts: SiteHosts = new Map()): string {
  const live: InputStatus | undefined = activeInputStatus(status)
  const config: Input | undefined = (live ? channel.inputs.find((i) => i.id === live.input_id) : undefined) ?? channel.inputs[0]
  if (!config && !live) return messages.t('originNone')
  const kind = live?.kind ?? config?.kind
  if (config?.slate || (status?.on_slate ?? false)) return messages.t('originSlate')
  const ip = senderIp(live?.remote_addr)
  switch (kind) {
    case 'srt_listen': {
      const port = config?.srt_listen?.port
      if (port) return ip ? messages.t('originSender', { proto: 'SRT', port: ` :${port}`, ip }) : messages.t('originListen', { proto: 'SRT', port })
      return ip ? messages.t('originSender', { proto: 'SRT', port: '', ip }) : messages.t('originListenNoPort', { proto: 'SRT' })
    }
    case 'rtmp_listen':
      return ip ? messages.t('originSender', { proto: 'RTMP', port: '', ip }) : messages.t('originListenNoPort', { proto: 'RTMP' })
    case 'srt_call': {
      const c = config?.srt_call
      return c ? messages.t('originPull', { host: `${c.host}:${c.port}` }) : messages.t('originPull', { host: ip || '—' })
    }
    case 'url':
      return config?.url ? urlOrigin(config.url.url, hosts) : messages.t('originUnreadable')
    case 'local_stream':
      return config?.local_stream?.name ? messages.t('originCopy', { name: config.local_stream.name }) : messages.t('originUnreadable')
    case 'file':
      return messages.t('originFile')
    case 'browser':
      return config?.browser?.url ? messages.t('originBrowser', { host: parseEndpoint(config.browser.url)?.host ?? '—' }) : messages.t('originUnreadable')
    default:
      return messages.t('originNone')
  }
}

/** Convenience for views: the origin by channel id from whatever the screen knows. */
export function originById(
  id: ID,
  channels: readonly Channel[],
  statusOf: (siteId: ID) => readonly ChannelStatus[] | undefined,
  streamOf: (channelId: ID) => LocalStream | undefined,
  hosts: SiteHosts,
): string | null {
  const ch = channels.find((c) => c.id === id)
  if (ch) return channelOrigin(ch, statusOf(ch.site_id)?.find((s) => s.channel_id === ch.id), hosts)
  const stream = streamOf(id)
  return stream ? streamOrigin(stream, hosts) : null
}
